import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDesignEntry } from '@/components/designs/DesignEntryChoice';
import { cn } from '@/lib/utils';

/**
 * The quiet way from the guided entry to the classic editor and Bulk
 * Creation where the host owns the navigation (embed layout `hostChrome`,
 * e.g. the Shopify admin): there is no design overview with the entry choice,
 * and the guided entry is the one to promote. A small text link that unfolds
 * the two modes in place; nothing is shown until the seller asks for it.
 */
export function AdvancedEntryLink({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { startClassic, startBulk } = useDesignEntry();
  const [open, setOpen] = useState(false);
  const listId = useId();

  return (
    <div
      className={cn('flex flex-col items-end gap-2', className)}
      data-testid="advanced-entry"
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((value) => !value)}
        className="dc-type-note inline-flex items-center gap-1 rounded-control px-1 text-[var(--mute)] hover:text-[var(--ink)]"
      >
        {t('creator.advancedEntry.toggle')}
        <ChevronDown
          aria-hidden="true"
          className={cn(
            'h-3.5 w-3.5 transition-transform',
            open && 'rotate-180'
          )}
        />
      </button>
      {open && (
        <ul id={listId} className="flex flex-wrap justify-end gap-2">
          <li>
            <AdvancedEntryOption
              title={t('seller.designs.entry.classic')}
              hint={t('seller.designs.entry.classicHint')}
              onClick={startClassic}
            />
          </li>
          <li>
            <AdvancedEntryOption
              title={t('seller.designs.entry.bulk')}
              hint={t('seller.designs.entry.bulkHint')}
              onClick={startBulk}
            />
          </li>
        </ul>
      )}
    </div>
  );
}

function AdvancedEntryOption({
  title,
  hint,
  onClick,
}: {
  title: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="dc-inset flex max-w-[260px] flex-col items-start gap-0.5 rounded-control px-3 py-2 text-left ring-1 ring-inset ring-[var(--line)] hover:bg-[var(--paper-2)]"
    >
      <span className="dc-type-card-title text-[var(--ink)]">{title}</span>
      <span className="dc-type-note text-[var(--mute)]">{hint}</span>
    </button>
  );
}
